import { z } from 'zod';
import { BUILD_CHANGE_TYPES, BUILD_STATES, BUILD_TYPES, RELEASE_STATUSES, RELEASE_TYPES } from '../enums';
import { isoDate, uuid } from './common';
import { itemKey } from './work-items';

export const createReleaseSchema = z.object({
  name: z.string().min(1).max(120),
  version: z.string().regex(/^\d+(\.\d+){0,3}$/, 'Semantic version such as 4.2.0'),
  type: z.enum(RELEASE_TYPES).default('minor'),
  targetDate: isoDate.nullable().optional(),
  releaseManagerId: uuid.nullable().optional(),
  qaManagerId: uuid.nullable().optional(),
  deputyRmId: uuid.nullable().optional(),
  deputyQaId: uuid.nullable().optional(),
  description: z.string().max(5000).nullable().optional(),
});
export type CreateReleaseInput = z.infer<typeof createReleaseSchema>;

export const updateReleaseSchema = createReleaseSchema.partial().extend({
  status: z.enum(RELEASE_STATUSES).optional(),
});
export type UpdateReleaseInput = z.infer<typeof updateReleaseSchema>;

const PACKAGE_VERSION_ID = /^04t[a-zA-Z0-9]{12}([a-zA-Z0-9]{3})?$/;

/** Manual build form (FR-REL-4). */
export const createBuildSchema = z.object({
  type: z.enum(BUILD_TYPES).default('beta'),
  label: z.string().max(60).optional(),
  versionNumber: z.string().max(40).optional(),
  packageVersionId: z.string().regex(PACKAGE_VERSION_ID, 'Salesforce 04t package version id').optional(),
  installationKey: z.string().max(200).optional(),
  gitBranch: z.string().max(200).optional(),
  gitTag: z.string().max(200).optional(),
  gitCommit: z.string().max(64).optional(),
  previousTag: z.string().max(200).optional(),
  ciRunUrl: z.string().url().optional(),
  coveragePct: z.number().min(0).max(100).optional(),
  reason: z.string().max(2000).optional(),
  /** Only when the subscriber My Domain or scratch org differs from the derived URL. */
  installUrlOverrideProd: z.string().url().nullable().optional(),
  installUrlOverrideSandbox: z.string().url().nullable().optional(),
  itemKeys: z.array(itemKey).optional(),
});
export type CreateBuildInput = z.infer<typeof createBuildSchema>;

export const updateBuildSchema = createBuildSchema
  .pick({ reason: true, coveragePct: true, installUrlOverrideProd: true, installUrlOverrideSandbox: true, ciRunUrl: true })
  .partial();
export type UpdateBuildInput = z.infer<typeof updateBuildSchema>;

/** CI build registration payload (ARCHITECTURE.md §8.3, FR-INT-1). */
export const ciBuildSchema = z.object({
  projectKey: z.string(),
  releaseVersion: z.string(),
  versionNumber: z.string().optional(),
  packageVersionId: z.string().regex(PACKAGE_VERSION_ID),
  installationKey: z.string().optional(),
  type: z.enum(BUILD_TYPES).default('beta'),
  git: z
    .object({
      branch: z.string().optional(),
      tag: z.string().optional(),
      commit: z.string().optional(),
      previousTag: z.string().optional(),
      provider: z.enum(['github', 'gitlab', 'bitbucket', 'other']).default('github'),
      repo: z.string().max(200).optional(),
      /** Raw commits between previousTag and tag (subject + body); stored in item_commit (§7.7). */
      commits: z
        .array(z.object({ sha: z.string().min(4).max(64), message: z.string().max(20000) }))
        .max(5000)
        .default([]),
      /** Pull requests merged in the range; titles and bodies are parsed for keys too. */
      pullRequests: z
        .array(z.object({ number: z.number().int(), title: z.string().max(500), body: z.string().max(20000).optional() }))
        .max(1000)
        .default([]),
    })
    .optional(),
  ciRunUrl: z.string().url().optional(),
  codeCoverage: z.number().min(0).max(100).optional(),
  reason: z.string().max(2000).optional(),
  markReadyForQa: z.boolean().default(false),
});
export type CiBuildInput = z.infer<typeof ciBuildSchema>;

export const buildStateSchema = z.object({ state: z.enum(BUILD_STATES), reason: z.string().max(2000).optional() });

export const buildItemsSchema = z.object({
  add: z.array(z.object({ key: itemKey, changeType: z.enum(BUILD_CHANGE_TYPES) })).default([]),
  remove: z.array(itemKey).default([]),
});
export type BuildItemsInput = z.infer<typeof buildItemsSchema>;

export const triageAssignSchema = z.object({
  role: z.enum(['dev', 'qa']),
  assignments: z.array(z.object({ key: itemKey, userId: uuid })).min(1).max(200),
});
export type TriageAssignInput = z.infer<typeof triageAssignSchema>;

/**
 * Install URLs are derived from the 04t id (§7.7); overrides win when the subscriber My Domain or
 * scratch org differs. `sf package install --package 04t…` is the supported CLI form.
 */
export function salesforceInstallUrls(
  packageVersionId: string,
  overrides: { prod?: string | null; sandbox?: string | null } = {},
) {
  return {
    production: overrides.prod || `https://login.salesforce.com/packaging/installPackage.apexp?p0=${packageVersionId}`,
    sandbox: overrides.sandbox || `https://test.salesforce.com/packaging/installPackage.apexp?p0=${packageVersionId}`,
    cli: `sf package install --package ${packageVersionId} --wait 10`,
  };
}

// ---------------------------------------------------------------------------------------------
// Git webhooks (FR-INT-2) and automated test results from CI (FR-INT-1)
// ---------------------------------------------------------------------------------------------

export const GIT_PROVIDERS = ['github', 'gitlab'] as const;
export const gitRulesSchema = z.object({
  /** Move linked items here when a (non-draft) PR/MR opens, e.g. in_code_review. */
  prOpenedStatusKey: z.string().max(60).nullable().optional(),
  /** Move linked items here when the PR/MR merges, e.g. ready_for_qa. */
  mergedStatusKey: z.string().max(60).nullable().optional(),
});
export const gitConnectionSchema = z.object({
  provider: z.enum(GIT_PROVIDERS),
  repo: z.string().min(3).max(200).regex(/^[\w.-]+(\/[\w.-]+)+$/, 'owner/repository'),
  rules: gitRulesSchema.default({}),
});
export const updateGitConnectionSchema = z.object({ rules: gitRulesSchema.optional(), enabled: z.boolean().optional() });
export const gitIdentitiesSchema = z.object({
  identities: z.array(z.object({ provider: z.enum(GIT_PROVIDERS), login: z.string().min(1).max(100) })).max(10),
});

export const ciTestResultsSchema = z.object({
  projectKey: z.string(),
  /** The exact build tested: its 04t or Kanbix build id. */
  packageVersionId: z.string().regex(PACKAGE_VERSION_ID).optional(),
  buildId: uuid.optional(),
  planId: uuid.optional(),
  environment: z.string().max(120).optional(),
  results: z
    .array(
      z.object({
        automationId: z.string().min(1).max(200),
        status: z.enum(['passed', 'failed', 'blocked', 'skipped']),
        durationSec: z.number().int().min(0).max(86400).optional(),
        message: z.string().max(8000).optional(),
      }),
    )
    .min(1)
    .max(5000),
  ciRunUrl: z.string().url().optional(),
  complete: z.boolean().default(false),
});
export type CiTestResultsInput = z.infer<typeof ciTestResultsSchema>;
